/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Card, Col, Collapse, Divider, Form, Input, Popconfirm, Radio, Row, Slider, Space, Table, Tooltip, Typography } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import FormSectionHeader from './FormSectionHeader';

// Misc imports
import { DEFAULT_PAGINATION_SIZE, PERMISSIONS_GROUP_ADMIN, PERMISSIONS_GROUP_EDITOR, PERMISSIONS_GROUP_VIEWER, SIMILARITY_ALGORITHMS } from '../../constants/main';
import { useUpdateBotMutation } from '../../store/bots/botsApi';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import useTheme from '../../utils/hooks/useTheme';
import Highlighter from 'react-highlight-words';
import Permissioned from '../Permissioned';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';
import useUserPermissions from '../../utils/hooks/useUserPermissions';
import replaceUrlParams from '../../utils/string/replaceUrlParams';
import { NAV_BOT } from '../../constants/navigation';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { useDragDetection } from '../../utils/hooks/useDragDetection';
import SideModal from '../Layout/SideModal';
import AddNlpKeywordsTable from '../Tables/AddNlpKeywordsTable';
import Prompt from './Prompt';
import { truncate } from '../../utils/string/truncate';

const NlpKeywordsView = () => {
  const { currentBotLanguage } = useBotLanguage();
  const screens = useBreakpoint();
  const isMobileSize = screens.xs;
  const { tenantId, orgId } = useParams();
  const { t } = useTranslation();
  const { borderRadiusBase, formProps } = useTheme();
  const userPermissions = useUserPermissions(orgId, tenantId);
  const { handleMouseDown, isDragging } = useDragDetection();
  const [form] = Form.useForm();

  const [searchFilter, setSearchFilter] = useState('');
  const [sortedInfo, setSortedInfo] = useState({ order: 'ascend', columnKey: 'name' });
  const [isSideModalOpen, setIsSideModalOpen] = useState(false);
  const [isSideModalClosing, setIsSideModalClosing] = useState(false);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [selectedAA, setSelectedAA] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [, updateState] = useState(); // Hook to force a component re-render
  const [pageSize, setPageSize] = useState(DEFAULT_PAGINATION_SIZE);

  const { data: bot, isLoading: isLoadingBot } = getCurrentQueryState('fetchBot', { orgId, tenantId });
  const { data: automatedAnswers, isLoading: isLoadingAutomatedAnswers } = getCurrentQueryState( 'fetchAutomatedAnswers', { orgId, tenantId });

  const [updateBot, { isLoading: isUpdatingBot }] = useUpdateBotMutation();

  const handleTableChange = (pagination, _filters, sorter) => {
    setSortedInfo(sorter);
    setPageSize(pagination.pageSize);
  };

  const onBotSettingsUpdate = async (values, action) => {
    const { similarityAlgorithm, similarityThreshold, intents } = values;
    const nluLocal = {
      intents: {
        ...(action !== 'delete' ? bot.settings?.nluLocal?.intents || {} : {}),
        ...intents
      },
      similarityThreshold: similarityAlgorithm === SIMILARITY_ALGORITHMS.EXACT_MATCH
        ? 1
        : similarityThreshold ?? bot.settings?.nluLocal?.similarityThreshold,
      similarityAlgorithm: similarityAlgorithm ? similarityAlgorithm : bot.settings?.nluLocal?.similarityAlgorithm
    };

    if (similarityAlgorithm === SIMILARITY_ALGORITHMS.EXACT_MATCH) {
      delete nluLocal.similarityAlgorithm;
    }

    const isSubmitSuccessful = await updateBot({ orgId, tenantId: bot?.id, data: { settings: { nluLocal } } }).unwrap();

    if (isSubmitSuccessful) {
      setHasPendingChanges(false);
      if (action !== 'select') {
        setIsSideModalOpen(false);
      }
    }
  };

  const handleDelete = (intentId) => {
    const intents = { ...bot.settings?.nluLocal?.intents };
    delete intents[intentId];
    onBotSettingsUpdate({ intents }, 'delete');
  };

  const onEditClick = (record) => {
    setSelectedAA(record?.id || null);
    setIsSideModalOpen(true);
    setIsEditMode(true);
  };

  const columns = [
    {
      title: t('Automated Answer'),
      key: 'name',
      ellipsis: true,
      sorter: (a, b) => a.name?.localeCompare(b.name),
      sortOrder: sortedInfo.columnKey === 'name' && sortedInfo.order,
      render: record => {
        return record.name ? (
          <Row justify="middle" wrap={false}>
            {
              !record.deleted ? (
                <Typography.Link
                  className="p-0 w-auto word-break-break-all pointer-events-all"
                  onClick={(event) => {
                    event.stopPropagation();
                    window.open(`${replaceUrlParams(NAV_BOT, { orgId, tenantId })}?view=behavior&tab=automated-answers&edit=${record.id}`, '_blank ');
                  }}
                >
                  <Tooltip placement="right" overlayStyle={{ maxWidth: 400 }} title={record.id}>
                    <Highlighter
                      highlightClassName="bg-highlight p-0"
                      searchWords={[searchFilter]}
                      autoEscape
                      className="text-overflow-ellipsis overflow-hidden white-space-nowrap"
                      textToHighlight={record.name}
                    />
                  </Tooltip>
                </Typography.Link>
              ) : (
                <Tooltip placement="right" overlayStyle={{ maxWidth: 400 }} title={record.id}>
                  <Highlighter
                    highlightClassName="bg-highlight p-0"
                    searchWords={[searchFilter]}
                    autoEscape
                    className="text-overflow-ellipsis overflow-hidden white-space-nowrap"
                    textToHighlight={record.name}
                  />
                </Tooltip>
              )
            }
          </Row>
        ) : <Typography.Text type="secondary">{t('(no name)')}</Typography.Text>;
      }
    },
    {
      title: t('Keywords'),
      key: 'keywords',
      render: (_text, record) => {
        const maxLength = isMobileSize ? 20 : 45;
        let textToDisplay = '';
        let remainingKeywords = [];

        for (let i = 0; i < record.keywords.length; i++) {
          const nextWord = record.keywords[i];
          const separator = textToDisplay ? ', ' : '';
          textToDisplay = `${textToDisplay}${separator}${nextWord}`;

          // Stop adding words after the limit is reached (always include last word even of it exceeds limit)
          if (i < record.keywords.length - 1 && textToDisplay.length >= maxLength) {
            remainingKeywords = record.keywords.slice(i + 1);
            break;
          }
        }

        return textToDisplay ? (
          <Row wrap={false} align="middle">
            <Highlighter
              highlightClassName="bg-highlight p-0"
              searchWords={[searchFilter]}
              autoEscape
              textToHighlight={truncate(textToDisplay, maxLength, '...')}
            />

            {
              remainingKeywords.length > 0 ? (
                <Typography.Text className={'white-space-nowrap bg-layout-gray ant-card-bordered border-radius-base px-2 ml-2'}>
                  +{remainingKeywords.length}
                </Typography.Text>
              ) : null
            }
          </Row>
        ) : null;
      }
    },
    {
      title: t('Actions'),
      key: 'actions',
      fixed: 'right',
      align: 'center',
      width: 140,
      hidden: !userPermissions.some(permission => PERMISSIONS_GROUP_VIEWER.includes(permission)),
      render: record => {
        if (userPermissions.some(permission => PERMISSIONS_GROUP_EDITOR.includes(permission))) {
          return (
            <Space onClick={(e) => e.stopPropagation()} size="small">
              <Tooltip title={t('Edit Keywords')}>
                <Button
                  shape="circle"
                  type="text"
                  icon={<EditOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditClick(record);
                  }}
                />
              </Tooltip>

              <Popconfirm
                title={
                  <Fragment>
                    <Typography.Text className="fw-bold">
                      {t('Delete Keywords?')}<br/>
                    </Typography.Text>
                    <Typography.Text>
                      {t('The keywords configuration will be permanently deleted')}
                    </Typography.Text>
                  </Fragment>
                }
                okText={t('Delete')}
                cancelText={t('Cancel')}
                placement="topRight"
                onConfirm={() => handleDelete(record.id)}
              >
                <Tooltip title={t('Delete Keywords')}>
                  <Button
                    shape="circle"
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Tooltip>
              </Popconfirm>
            </Space>
          );
        }
      }
    }
  ];

  return (
    <Card>
      <Collapse defaultActiveKey={['1']} ghost>
        <Collapse.Panel
          showArrow={false}
          collapsible="icon"
          header={<FormSectionHeader title={t('Keywords Settings')} collapsible={false} />}
          key="1"
        >
          <Form
            form={form}
            onFinish={onBotSettingsUpdate}
            onValuesChange={(_, allFields) => {
              setHasPendingChanges(true);
              updateState(allFields);
            }}
            initialValues={{
              similarityAlgorithm: bot.settings?.nluLocal?.similarityAlgorithm ? bot.settings.nluLocal.similarityAlgorithm : bot.settings?.nluLocal?.similarityThreshold === 1 ? SIMILARITY_ALGORITHMS.EXACT_MATCH : '',
              similarityThreshold: bot.settings?.nluLocal?.similarityThreshold || 0
            }}
          >
            <Row
              wrap
              gutter={formProps.rowGutter}
              className="mb-6"
            >
              <Col xs={formProps.mainColSpan}>
                <Typography.Paragraph className="mb-0">{t('Text Similarity Algorithm')}</Typography.Paragraph>

                <Typography.Paragraph className="fs-sm" type="secondary" strong>
                  {t('Select which algorithm to use for text similarity. (An algorithm that measures how closely two pieces of text resemble each other)')}
                </Typography.Paragraph>
              </Col>

              <Col xs={formProps.secondaryColSpan} className="overflow-x-hidden">
                <Form.Item name="similarityAlgorithm">
                  <Radio.Group
                    onChange={(event) => {
                      if (event.target.value === SIMILARITY_ALGORITHMS.EXACT_MATCH) {
                        form.setFieldValue('similarityThreshold', 1);
                      } else if (event.target.value === bot.settings.nluLocal.similarityAlgorithm) {
                        form.setFieldValue('similarityThreshold', bot.settings.nluLocal.similarityThreshold);
                      } else {
                        form.setFieldValue('similarityThreshold', 0);
                      }
                    }}
                  >
                    <Space direction="vertical">
                      <Radio value={SIMILARITY_ALGORITHMS.EXACT_MATCH} className="mb-2">
                        {t('Exact Match')}
                      </Radio>

                      <Radio value={SIMILARITY_ALGORITHMS.JARO_WINKLER} className="mb-2">
                        {t('Jaro Winkler')}
                      </Radio>

                      <Radio value={SIMILARITY_ALGORITHMS.LEVENSHTEIN}>
                        {t('Damerau Levenshtein')}
                      </Radio>
                    </Space>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>

            <Row
              wrap
              gutter={formProps.rowGutter}
              className="mb-4"
            >
              <Col xs={formProps.mainColSpan}>
                <Typography.Paragraph className="mb-0">{t('Algorithm Threshold')}</Typography.Paragraph>
                <Typography.Paragraph className="fs-sm" type="secondary" strong>{t('Defines the sensitivity level of the text similarity algorithm.')}</Typography.Paragraph>
              </Col>

              <Col xs={formProps.secondaryColSpan}>
                <Form.Item name={'similarityThreshold'}>
                  <Slider
                    min={0}
                    max={1}
                    step={0.01}
                    className="custom-slider"
                    style={{ maxWidth: formProps.maxInputWidth }}
                    disabled={form.getFieldValue('similarityAlgorithm') === SIMILARITY_ALGORITHMS.EXACT_MATCH}
                    marks={{ 0: '0', 1: '1' }}
                    handleStyle={{ borderRadius: borderRadiusBase }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>

          {
            hasPendingChanges ? (
              <Row style={{ maxWidth: 740 }} justify="end">
                <Divider/>

                <Button
                  type="default"
                  className={'mr-4'}
                  size="large"
                  onClick={() => {
                    form.resetFields();
                    setHasPendingChanges(false);
                  }}
                >
                  {t('Cancel')}
                </Button>

                <Button
                  disabled={!hasPendingChanges || isLoadingBot}
                  type="primary"
                  size="large"
                  loading={isLoadingBot}
                  onClick={form.submit}
                >
                  {t('Save Changes')}
                </Button>
              </Row>
            ) : null
          }
        </Collapse.Panel>
      </Collapse>

      <Collapse defaultActiveKey={['1']} ghost>
        <Collapse.Panel
          showArrow={false}
          collapsible="icon"
          header={<FormSectionHeader title={t('Keywords')} collapsible={false} />}
          key="1"
        >
          <Fragment>
            <Row gutter={24} wrap={isMobileSize}>
              <Col flex="auto" className="mb-2">
                <Input
                  size="large"
                  style={{ maxWidth: isMobileSize ? '100%' : 280 }}
                  placeholder={t('Search in contents')}
                  allowClear
                  prefix={<SearchOutlined className="text-muted" />}
                  className="w-100"
                  onChange={(e) => {
                    setSearchFilter(e.target.value);
                  }}
                />
              </Col>

              <Col
                flex={isMobileSize ? 'auto' : 'none'}
                className="mb-2"
              >
                <Permissioned
                  allowedPermissions={PERMISSIONS_GROUP_ADMIN}
                  currentPermissions={userPermissions}
                  fallbackComponent={null}
                >
                  <Button
                    icon={<PlusOutlined />}
                    className="float-right mb-2"
                    size="large"
                    type="primary"
                    block={isMobileSize}
                    onClick={() => onEditClick(null)}
                    loading={isUpdatingBot || isLoadingAutomatedAnswers}
                  >
                    {t('Add Keywords')}
                  </Button>
                </Permissioned>
              </Col>
            </Row>

            <Table
              size="small"
              pagination={{
                size: 'default',
                pageSize: pageSize,
                showSizeChanger: true
              }}
              onRow={(record) => ({
                onMouseDown: handleMouseDown,
                onClick: () => {
                  if(!isDragging() && userPermissions.some(permission => PERMISSIONS_GROUP_EDITOR.includes(permission))) {
                    onEditClick(record);
                  }
                }
              })}
              rowClassName="cursor-pointer"
              onChange={handleTableChange}
              dataSource={
                bot.settings?.nluLocal?.intents ? (
                  Object.entries(bot.settings.nluLocal.intents)
                  // Filter out intents that don't match searchFilter, neither answer name nor any of the keywords
                    .filter(([id, keywords]) => {
                      return (
                        !searchFilter
                        || automatedAnswers?.items.find(answer => answer.id === id)?.name[currentBotLanguage]?.toLowerCase().includes(searchFilter.toLowerCase())
                        || keywords.some(keyword => keyword.toLowerCase().includes(searchFilter.toLowerCase()))
                      );
                    })
                    .map(([id, keywords]) => {
                      const matchedAnswer = automatedAnswers?.items.find(answer => answer.id === id);
                      const name = matchedAnswer?.name?.[currentBotLanguage] || id;
                      const deleted = !matchedAnswer?.name?.[currentBotLanguage];

                      return { id, keywords, name, deleted };
                    })
                ) : []
              }
              columns={columns}
              loading={isLoadingBot || isLoadingAutomatedAnswers}
            />
          </Fragment>
        </Collapse.Panel>
      </Collapse>

      <SideModal
        open={isSideModalOpen}
        onClose={() => setIsSideModalClosing(true)}
        title={isEditMode ? t('Edit Keywords of Automated Answer') : t('Add Keywords to Automated Answer')}
        bodyClassName="p-0"
        destroyOnClose
      >
        <AddNlpKeywordsTable
          onSave={(values, action) => onBotSettingsUpdate(values, action)}
          intents={bot.settings?.nluLocal?.intents || {}}
          selectedAA={selectedAA}
          automatedAnswers={automatedAnswers}
          isUpdating={isUpdatingBot}
          onCancel={() => {
            setSelectedAA(null);
            setIsSideModalOpen(false);
            setIsSideModalClosing(false);
          }}
          onModalDismiss={() => setIsSideModalClosing(false)}
          isSideModalClosing={isSideModalClosing}
          bodyClassName="pt-6 flex-grow-1"
          footerClassName="ant-drawer-footer"
        />
      </SideModal>

      <Prompt
        when={hasPendingChanges}
        onConfirm={form.submit}
        okText={t('Save Changes')}
        cancelText={t('Quit without saving')}
        title={t('Save Changes?')}
        message={t('Your unsaved changes will be lost. Save changes before closing?')}
      />

    </Card>
  );
};

export default NlpKeywordsView;
